/**
 * NoteEditor Store - Refrakt-based store for the note editor
 *
 * This store manages note edit form state:
 * - Form fields (title, content, type, tags, meta)
 * - Autosave logic with debouncing
 * - Save API calls via effects
 *
 * Communication with parent:
 * - Component emits DOM events for major changes
 */
import { pipe, store, updateUnknown } from "refrakt";
import { fx } from "refrakt/middleware/fx.js";
import { logger } from "refrakt/middleware/logger.js";
import { customEvent } from "../../util/event.ts";
import { type Note } from "../../../models/note.ts";
import { type Ulid } from "../../../models/ulid.ts";
import { type Slug } from "../../../models/slug.ts";
import { nowIsoString } from "../../../util/date.ts";
import * as NotesService from "../../services/notes.ts";
import { sleep } from "../../../util/sleep.ts";
import { type Json } from "../../../generated/supabase.ts";

export type Model = {
  /** Auth token for API calls - set by component */
  accessToken: string | undefined;
  saveToken: number;
  slug: string | undefined;
  projectId: string | undefined;
  type: string;
  title: string;
  content: string;
  meta: Json;
  tags: string[];
  /** Raw JSON string for meta editing */
  metaJsonString: string;
  /** JSON validation error */
  metaJsonError: string | undefined;
  sourceSlugs: Slug[];
  sourceUrls: string[];
  confidence: number;
  authorUserId: string | undefined;
  authorAgentId: string | undefined;
  createdAt: string | undefined;
  updatedAt: string | undefined;
  isLoading: boolean;
  isSaving: boolean;
  /** Last error message */
  lastError: string | undefined;
};

export const model = ({
  accessToken = undefined,
  saveToken = 0,
  slug = undefined,
  projectId = undefined,
  type = "note",
  title = "",
  content = "",
  meta = {},
  tags = [],
  metaJsonString = "{}",
  metaJsonError = undefined,
  sourceSlugs = [],
  sourceUrls = [],
  confidence = 0.5,
  authorUserId = undefined,
  authorAgentId = undefined,
  createdAt = undefined,
  updatedAt = undefined,
  isLoading = false,
  isSaving = false,
  lastError = undefined,
}: Partial<Model> = {}): Model => {
  return {
    accessToken,
    saveToken,
    slug,
    projectId,
    type,
    title,
    content,
    meta,
    tags,
    metaJsonString,
    metaJsonError,
    sourceSlugs,
    sourceUrls,
    confidence,
    authorUserId,
    authorAgentId,
    createdAt,
    updatedAt,
    isLoading,
    isSaving,
    lastError,
  };
};

export type SetNoteAction = {
  type: "set-note";
  note: Note;
};

export type SetAccessTokenAction = {
  type: "set-access-token";
  accessToken: string | undefined;
};

export type SetTitleAction = {
  type: "set-title";
  value: string;
  accessToken: string | undefined;
};

export type SetContentAction = {
  type: "set-content";
  value: string;
  accessToken: string | undefined;
};

export type SetTypeAction = {
  type: "set-type";
  value: string;
  accessToken: string | undefined;
};

export type SetTagsAction = {
  type: "set-tags";
  value: string[];
  accessToken: string | undefined;
};

export type SetMetaJsonAction = {
  type: "set-meta-json";
  value: string;
  accessToken: string | undefined;
};

export type ScheduleSaveAction = {
  type: "schedule-save";
  accessToken: string | undefined;
};

export type CheckSaveAction = {
  type: "check-save";
  saveToken: number;
  accessToken: string | undefined;
};

export type ExecuteSaveAction = {
  type: "execute-save";
  note: Note;
  accessToken: string | undefined;
};

export type SaveSuccessAction = {
  type: "save-success";
  slug: Slug;
};

export type SaveErrorAction = {
  type: "save-error";
  error: string;
};

export type SetLoadingAction = {
  type: "set-loading";
  isLoading: boolean;
};

export type LoadNoteAction = {
  type: "load-note";
  slug: Slug | undefined;
  projectId: Ulid | undefined;
  accessToken: string | undefined;
};

export type LoadNoteSuccessAction = {
  type: "load-note-success";
  note: Note;
};

export type LoadNoteNotFoundAction = {
  type: "load-note-not-found";
  slug: Slug;
  projectId: Ulid;
};

export type LoadNoteErrorAction = {
  type: "load-note-error";
  error: string;
};

export type ResetAction = {
  type: "reset";
};

export type Action =
  | SetNoteAction
  | SetAccessTokenAction
  | SetTitleAction
  | SetContentAction
  | SetTypeAction
  | SetTagsAction
  | SetMetaJsonAction
  | ScheduleSaveAction
  | CheckSaveAction
  | ExecuteSaveAction
  | SaveSuccessAction
  | SaveErrorAction
  | SetLoadingAction
  | LoadNoteAction
  | LoadNoteSuccessAction
  | LoadNoteNotFoundAction
  | LoadNoteErrorAction
  | ResetAction;

/** Create a draft note */
export const draftNote = (slug: string, projectId: string): Note => {
  const now = nowIsoString();
  return {
    slug,
    type: "note",
    title: "",
    content: "",
    tags: [],
    meta: {},
    confidence: 0.5,
    source_slugs: [],
    source_urls: [],
    created_at: now,
    updated_at: now,
    project_id: projectId,
  };
};

/** Convert edit form model to Note */
export const modelToNote = (model: Model): Note | undefined => {
  if (model.slug === undefined || model.projectId === undefined) {
    return undefined;
  }

  const now = nowIsoString();

  return {
    slug: model.slug,
    project_id: model.projectId,
    type: model.type,
    title: model.title,
    content: model.content,
    meta: model.meta as Note["meta"],
    tags: model.tags,
    source_slugs: model.sourceSlugs,
    source_urls: model.sourceUrls,
    confidence: model.confidence,
    author_user_id: model.authorUserId,
    author_agent_id: model.authorAgentId,
    created_at: model.createdAt ?? now,
    updated_at: now,
  };
};

const noteToModel = (note: Note): Model => {
  const metaJsonString = JSON.stringify(note.meta ?? {}, null, 2);
  return {
    slug: note.slug,
    projectId: note.project_id,
    type: note.type,
    title: note.title,
    content: note.content,
    meta: (note.meta ?? {}) as Json,
    tags: note.tags ?? [],
    metaJsonString,
    metaJsonError: undefined,
    sourceSlugs: note.source_slugs ?? [],
    sourceUrls: note.source_urls ?? [],
    confidence: note.confidence ?? 0.5,
    authorUserId: note.author_user_id ?? undefined,
    authorAgentId: note.author_agent_id ?? undefined,
    createdAt: note.created_at,
    updatedAt: note.updated_at,
    isLoading: false,
  } as Model;
};

const updateSetNote = (state: Model, action: SetNoteAction): Model => {
  return {
    ...state,
    ...noteToModel(action.note),
  };
};

const updateSetAccessToken = (
  state: Model,
  action: SetAccessTokenAction,
): Model => ({
  ...state,
  accessToken: action.accessToken,
});

const updateSetTitle = (state: Model, action: SetTitleAction): Model => ({
  ...state,
  title: action.value,
});

const updateSetContent = (state: Model, action: SetContentAction): Model => ({
  ...state,
  content: action.value,
});

const updateSetType = (state: Model, action: SetTypeAction): Model => ({
  ...state,
  type: action.value,
});

const updateSetTags = (state: Model, action: SetTagsAction): Model => ({
  ...state,
  tags: action.value,
});

const updateSetMetaJson = (state: Model, action: SetMetaJsonAction): Model => {
  try {
    const parsed = JSON.parse(action.value);
    return {
      ...state,
      metaJsonString: action.value,
      meta: parsed,
      metaJsonError: undefined,
    };
  } catch (e) {
    return {
      ...state,
      metaJsonString: action.value,
      metaJsonError: (e as Error).message,
    };
  }
};

const updateScheduleSave = (state: Model): Model => ({
  ...state,
  saveToken: Date.now(),
});

const updateExecuteSave = (state: Model): Model => ({
  ...state,
  isSaving: true,
  lastError: undefined,
});

const updateSaveSuccess = (
  state: Model,
  _action: SaveSuccessAction,
): Model => ({
  ...state,
  isSaving: false,
});

const updateSaveError = (state: Model, action: SaveErrorAction): Model => ({
  ...state,
  isSaving: false,
  lastError: action.error,
});

const updateSetLoading = (state: Model, action: SetLoadingAction): Model => ({
  ...state,
  isLoading: action.isLoading,
});

const updateLoadNote = (state: Model, action: LoadNoteAction): Model => {
  // Only transition to loading if we have both slug and projectId
  if (!action.slug || !action.projectId) {
    return state;
  }
  return {
    ...state,
    isLoading: true,
    lastError: undefined,
  };
};

const updateLoadNoteSuccess = (
  state: Model,
  action: LoadNoteSuccessAction,
): Model =>
  updateSetNote(
    { ...state, isLoading: false },
    {
      type: "set-note",
      note: action.note,
    },
  );

const updateLoadNoteNotFound = (
  state: Model,
  action: LoadNoteNotFoundAction,
): Model => {
  const draft = draftNote(action.slug, action.projectId);
  return updateSetNote(
    { ...state, isLoading: false },
    {
      type: "set-note",
      note: draft,
    },
  );
};

const updateLoadNoteError = (
  state: Model,
  action: LoadNoteErrorAction,
): Model => ({
  ...state,
  isLoading: false,
  lastError: action.error,
});

export const update = (state: Model, action: Action): Model => {
  switch (action.type) {
    case "set-note":
      return updateSetNote(state, action);
    case "set-access-token":
      return updateSetAccessToken(state, action);
    case "set-title":
      return updateSetTitle(state, action);
    case "set-content":
      return updateSetContent(state, action);
    case "set-type":
      return updateSetType(state, action);
    case "set-tags":
      return updateSetTags(state, action);
    case "set-meta-json":
      return updateSetMetaJson(state, action);
    case "schedule-save":
      return updateScheduleSave(state);
    case "check-save":
      // State doesn't change - effect handles this
      return state;
    case "execute-save":
      return updateExecuteSave(state);
    case "save-success":
      return updateSaveSuccess(state, action);
    case "save-error":
      return updateSaveError(state, action);
    case "set-loading":
      return updateSetLoading(state, action);
    case "load-note":
      return updateLoadNote(state, action);
    case "load-note-success":
      return updateLoadNoteSuccess(state, action);
    case "load-note-not-found":
      return updateLoadNoteNotFound(state, action);
    case "load-note-error":
      return updateLoadNoteError(state, action);
    case "reset":
      return model();
    default:
      return updateUnknown(state, action);
  }
};

type GetState = () => Model;

async function* fxScheduleSave(
  saveToken: number,
  accessToken: string | undefined,
): AsyncGenerator<Action> {
  // Wait for debounce period
  await sleep(2000);

  // Dispatch check-save with the current model save token
  yield { type: "check-save", saveToken, accessToken };
}

async function* fxCheckSave(
  getState: GetState,
  action: CheckSaveAction,
): AsyncGenerator<Action> {
  const state = getState();

  // Ignore if a newer save was scheduled
  if (state.saveToken > action.saveToken) {
    return;
  }

  // Don't save if meta JSON has error
  if (state.metaJsonError) {
    return;
  }

  const note = modelToNote(state);

  if (note) {
    yield { type: "execute-save", note, accessToken: action.accessToken };
  }
}

export const saveSuccessEvent = customEvent<Slug>("save-success", {
  bubbles: true,
  composed: true,
});

export const saveErrorEvent = customEvent<string>("save-error", {
  bubbles: true,
  composed: true,
});

async function* fxExecuteSave(
  _getState: GetState,
  action: ExecuteSaveAction,
  host: HTMLElement,
): AsyncGenerator<Action> {
  const { note, accessToken } = action;

  const result = await NotesService.upsertNote(
    accessToken,
    note.project_id,
    note.slug,
    note,
  );

  if (result.ok) {
    yield { type: "save-success", slug: note.slug! };
    host.dispatchEvent(saveSuccessEvent(note.slug!));
  } else {
    yield { type: "save-error", error: result.error };
    host.dispatchEvent(saveErrorEvent(result.error));
  }
}

async function* fxLoadNote(
  _getState: GetState,
  action: LoadNoteAction,
): AsyncGenerator<Action> {
  const { slug, projectId, accessToken } = action;

  // Don't load if we don't have both slug and projectId
  if (!slug || !projectId) {
    return;
  }

  const result = await NotesService.getNote(accessToken, projectId, slug);

  if (result.ok) {
    if (result.value) {
      // Found the note - load it
      yield { type: "load-note-success", note: result.value };
    } else {
      // Not found - create draft
      yield { type: "load-note-not-found", slug, projectId };
    }
  } else {
    yield { type: "load-note-error", error: result.error };
  }
}

export const fxNoteEdit = async function* (
  getState: GetState,
  action: Action,
  host: HTMLElement,
): AsyncGenerator<Action> {
  switch (action.type) {
    case "load-note":
      return yield* fxLoadNote(getState, action);
    case "schedule-save": {
      const state = getState();
      return yield* fxScheduleSave(state.saveToken, action.accessToken);
    }
    case "check-save":
      return yield* fxCheckSave(getState, action);
    case "execute-save":
      return yield* fxExecuteSave(getState, action, host);
    case "set-title":
      return yield { type: "schedule-save", accessToken: action.accessToken };
    case "set-content":
      return yield { type: "schedule-save", accessToken: action.accessToken };
    case "set-type":
      return yield { type: "schedule-save", accessToken: action.accessToken };
    case "set-tags":
      return yield { type: "schedule-save", accessToken: action.accessToken };
    case "set-meta-json":
      return yield { type: "schedule-save", accessToken: action.accessToken };
    default:
      return;
  }
};

export type NoteEditStore = ReturnType<typeof createNoteEditStore>;

export const createNoteEditStore = (initial: Model, host: HTMLElement) => {
  return pipe(
    store(update, initial),
    logger({
      log: true,
      prefix: "note-editor ",
    }),
    fx(fxNoteEdit, host),
  );
};
